import { __rest } from "tslib";
import React, { createRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Close, CloudSave, DeleteEditor, Edit, Gear, Mic, Pause, Play, Save, Stop, Trash, } from "../Icon";
import "./Button.css";
function Button({ disabled, className, children, label, onClick, linkTo, needsImmediateFocus, ariaControls, href, }) {
    const buttonRef = createRef();
    const [hasFocused, setHasFocused] = useState(false);
    useEffect(() => {
        if (needsImmediateFocus) {
            const element = buttonRef.current;
            if (element && !hasFocused) {
                element.focus();
                setHasFocused(true);
            }
        }
    }, [needsImmediateFocus, hasFocused, buttonRef]);
    if (href) {
        if (onClick) {
            throw new Error("Buttons with href props cannot have click handlers");
        }
        if (linkTo) {
            throw new Error("Buttons with href props cannot have linkTo prop");
        }
        return (React.createElement("a", { href: href, className: `Button ${className || ""}  ${disabled ? "--disabled" : ""}` },
            label ? React.createElement("div", { className: "hide-text" }, label) : undefined,
            children));
    }
    if (linkTo) {
        if (onClick) {
            throw new Error("Buttons with linkTo props cannot have click handlers");
        }
        return (React.createElement(Link, { to: linkTo, className: `Button ${className || ""}  ${disabled ? "--disabled" : ""}` },
            label ? React.createElement("div", { className: "hide-text" }, label) : undefined,
            children));
    }
    return (React.createElement("button", { className: `Button ${className || ""}`, disabled: disabled, onClick: !disabled ? onClick : undefined, "aria-label": label, "aria-controls": ariaControls, ref: buttonRef }, children));
}
function OvalButton({ disabled, type, filled, label, linkTo, onClick, iconPosition, icon, children, needsImmediateFocus, ariaControls, href, }) {
    return (React.createElement(Button, { className: `--oval --oval-${type} --${filled ? "filled" : "unfilled"} --icon-${iconPosition || "left"}`, disabled: disabled, onClick: onClick, linkTo: linkTo, label: label, needsImmediateFocus: needsImmediateFocus, ariaControls: ariaControls, href: href },
        icon ? React.createElement("span", { className: "Button_icon" }, icon) : undefined,
        React.createElement("span", { className: "Button_label" }, children)));
}
export function PrimaryButton(props) {
    return React.createElement(OvalButton, Object.assign({}, props, { type: "primary" }));
}
export function BackButton(props) {
    return (React.createElement(OvalButton, Object.assign({}, props, { icon: React.createElement(ArrowLeft, null), iconPosition: "left", type: props.type || "primary" })));
}
export function GreyedButton(props) {
    return React.createElement(OvalButton, Object.assign({}, props, { type: "greyed-out" }));
}
export function UploadButton(props) {
    const { t } = useTranslation();
    function onChange(e) {
        if (props.onFile) {
            props.onFile(e.target.files[0]);
        }
    }
    function onClick(e) {
        if (props.onClick) {
            e.preventDefault();
            props.onClick();
        }
    }
    return (React.createElement("div", { className: "Button_file-wrapper" },
        React.createElement("label", { htmlFor: "Button_file", className: `Button ${props.disabled ? "--disabled" : ""} --oval --oval-primary --unfilled --icon-left --file` },
            React.createElement("span", { className: "Button_icon" },
                React.createElement(CloudSave, null)),
            React.createElement("span", { className: "Button_label" }, props.isWideScreen ? t("button.upload") : t("button.upload_short"))),
        React.createElement("input", { id: "Button_file", className: "hide-text Button_file", type: "file", accept: "audio/*", onChange: onChange, onClick: onClick })));
}
export function NextButton(props) {
    return (React.createElement(OvalButton, Object.assign({}, props, { icon: React.createElement(ArrowRight, null), iconPosition: "right", type: "primary" })));
}
export function CancelButton(props) {
    return (React.createElement(OvalButton, Object.assign({}, props, { icon: React.createElement(Close, null), iconPosition: "left", type: props.type || "primary" })));
}
export function SecondaryButton(props) {
    return React.createElement(OvalButton, Object.assign({}, props, { type: "secondary" }));
}
export function TertiaryButton(props) {
    return React.createElement(OvalButton, Object.assign({}, props, { type: "tertiary" }));
}
export function FilledRoundButton(props) {
    return React.createElement(OvalButton, Object.assign({}, props, { type: "filled-round" }));
}
export function CircleButton({ disabled, label, onClick, className, size, icon, needsImmediateFocus, ariaControls, href, }) {
    return (React.createElement(Button, { className: `--circle ${className || ""} --circle-${size}`, disabled: disabled, onClick: onClick, label: label, needsImmediateFocus: needsImmediateFocus, ariaControls: ariaControls, href: href }, icon ? React.createElement("span", { className: "Button_icon" }, icon) : undefined));
}
export function RecordButton(props) {
    const { t } = useTranslation();
    const { className, active } = props, remainingProps = __rest(props, ["className", "active"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-record ${className || ""}`, icon: active ? React.createElement(Stop, null) : React.createElement(Mic, null), label: active ? t("button.stop") : t("button.record") })));
}
export function PlayButton(props) {
    const { t } = useTranslation();
    const { className, active } = props, remainingProps = __rest(props, ["className", "active"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-play ${className || ""}`, icon: active ? React.createElement(Pause, null) : React.createElement(Play, null), label: active ? t("button.pause") : t("button.play") })));
}
export function PlayButtonUnfilled(props) {
    const { t } = useTranslation();
    const { className, active } = props, remainingProps = __rest(props, ["className", "active"]);
    return (React.createElement(CircleButtonUnfilled, Object.assign({}, remainingProps, { className: `--circle-play ${className || ""}`, icon: active ? React.createElement(Pause, null) : React.createElement(Play, null), label: active ? t("button.pause") : t("button.play") })));
}
export function DeleteButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-delete ${className || ""}`, icon: React.createElement(Trash, null), label: t("button.delete") })));
}
export function DeleteButtonUnfilled(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButtonUnfilled, Object.assign({}, remainingProps, { className: `--circle-delete ${className || ""}`, icon: React.createElement(Trash, null), label: t("button.delete") })));
}
function CircleButtonUnfilled({ label, onClick, className, icon, size, disabled, href, }) {
    return (React.createElement(CircleButton, { icon: icon, size: size, className: `--unfilled ${className || ""}`, onClick: onClick, label: label, disabled: disabled, href: href }));
}
export function SecondaryDeleteButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-delete --unfilled ${className || ""}`, icon: React.createElement(Trash, null), label: t("button.delete") })));
}
export function GearButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButtonUnfilled, Object.assign({}, remainingProps, { className: `--circle-gear ${className || ""}`, icon: React.createElement(Gear, null), label: t("button.account") })));
}
export function EditButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-edit --unfilled ${className || ""}`, icon: React.createElement(Edit, null), label: t("button.edit") })));
}
export function CloseButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-close --unfilled ${className || ""}`, icon: React.createElement(Close, null), label: t("button.close") })));
}
export function SaveButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-save --unfilled ${className || ""}`, icon: React.createElement(Save, null), label: t("button.save") })));
}
export function UpButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-up --unfilled ${className || ""}`, icon: React.createElement(ArrowLeft, null), label: t("button.move_up") })));
}
export function MinimalButton({ onClick, children, size }) {
    return (React.createElement("button", { className: `button-minimal ${size ? `--${size}` : ""}`, onClick: onClick }, children));
}
export function DeleteEditorButton(props) {
    const { t } = useTranslation();
    const { className } = props, remainingProps = __rest(props, ["className"]);
    return (React.createElement(CircleButton, Object.assign({}, remainingProps, { className: `--circle-delete-editor ${className || ""}`, icon: React.createElement(DeleteEditor, null), label: t("button.delete") })));
}
//# sourceMappingURL=Button.js.map